import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowLeft, Save, RefreshCw, Eye, Sparkles, 
  CheckCircle, ExternalLink, Loader2, Wand2,
  ImageIcon, FileText, Zap, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// CSS to override WordPress inline styles in content preview
const contentPreviewStyles = `
  .content-preview-area,
  .content-preview-area * {
    color: #e2e8f0 !important;
  }
  .content-preview-area h1,
  .content-preview-area h2,
  .content-preview-area h3,
  .content-preview-area h4,
  .content-preview-area b,
  .content-preview-area strong {
    color: #ffffff !important;
    font-weight: 600;
  }
  .content-preview-area h2 {
    font-size: 1.25rem;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }
  .content-preview-area h3 {
    font-size: 1.1rem;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }
  .content-preview-area p {
    margin-bottom: 1rem;
    line-height: 1.75;
  }
  .content-preview-area img {
    border-radius: 0.5rem;
    margin: 1rem 0;
    max-width: 100%;
    height: auto;
  }
  .content-preview-area a {
    color: #60a5fa !important;
    text-decoration: none;
  }
  .content-preview-area a:hover {
    text-decoration: underline;
  }
  .content-preview-area ul,
  .content-preview-area ol {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
  }
  .content-preview-area li {
    margin-bottom: 0.25rem;
  }
`;

const PostEditorPage = () => {
  const { postId } = useParams();
  const [searchParams] = useSearchParams();
  const configId = searchParams.get('config_id');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [post, setPost] = useState(null);
  const [aiFeatures, setAiFeatures] = useState({ post_editing_enabled: false, ai_optimization_enabled: false });
  const [hasApiKeys, setHasApiKeys] = useState({ gemini: false, airbrush: false });

  // Editing state
  const [editedTitle, setEditedTitle] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // AI Optimization state
  const [optimizing, setOptimizing] = useState(false);
  const [optimizationStep, setOptimizationStep] = useState('');
  const [optimizationStats, setOptimizationStats] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, [postId, configId]);

  useEffect(() => {
    if (post) {
      const titleChanged = editedTitle !== (post.title?.raw || '');
      const contentChanged = editedContent !== (post.content?.raw || '');
      setHasChanges(titleChanged || contentChanged);
    }
  }, [editedTitle, editedContent, post]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [featuresRes, keysRes] = await Promise.all([
        axios.get(`${API}/ai/features/status`),
        axios.get(`${API}/ai/keys`, { headers })
      ]);

      setAiFeatures(featuresRes.data);
      setHasApiKeys({
        gemini: keysRes.data.has_gemini_key,
        airbrush: keysRes.data.has_airbrush_key
      });

      if (!featuresRes.data.post_editing_enabled) {
        toast.error('Post editing feature is not enabled');
        navigate('/app/posts');
        return;
      }

      const postRes = await axios.get(`${API}/posts/${postId}${configId ? `?config_id=${configId}` : ''}`, { headers });
      setPost(postRes.data);
      setEditedTitle(postRes.data.title?.raw || '');
      setEditedContent(postRes.data.content?.raw || '');

    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Post editing is not enabled');
        navigate('/app/posts');
      } else if (error.response?.status === 404) {
        toast.error('Post not found');
        navigate('/app/posts');
      } else {
        toast.error('Failed to load post');
      }
    } finally {
      setLoading(false);
    }
  };

  const savePost = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {};

      if (editedTitle !== post.title?.raw) payload.title = editedTitle;
      if (editedContent !== post.content?.raw) payload.content = editedContent;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setSaving(false);
        return;
      }

      await axios.put(
        `${API}/posts/${postId}${configId ? `?config_id=${configId}` : ''}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Post saved successfully to WordPress!');
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const runAIOptimization = async () => {
    if (!hasApiKeys.gemini || !hasApiKeys.airbrush) {
      toast.error('Please configure both Gemini and Airbrush API keys in Settings');
      return;
    }

    setOptimizing(true);
    setOptimizationStep('Analyzing content...');
    setOptimizationStats(null);

    try {
      const token = localStorage.getItem('token');
      
      // Simulate progress steps for better UX
      const steps = [
        'Analyzing content...',
        'Generating optimized title...',
        'Improving content structure...',
        'Creating AI images...',
        'Finalizing optimization...'
      ];
      
      let stepIndex = 0;
      const stepInterval = setInterval(() => {
        stepIndex = (stepIndex + 1) % steps.length;
        setOptimizationStep(steps[stepIndex]);
      }, 3000);

      const response = await axios.post(
        `${API}/ai/optimize-post/${postId}`,
        { config_id: configId },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 180000 // 3 minutes timeout for image generation
        }
      );

      clearInterval(stepInterval);

      if (response.data.success) {
        setEditedTitle(response.data.optimized.title);
        setEditedContent(response.data.optimized.content);
        setOptimizationStats(response.data.stats);
        setOptimizationStep('');
        
        // Show warnings if any
        if (response.data.warnings && response.data.warnings.length > 0) {
          // Check if it's a quota/rate limit issue
          const hasQuotaError = response.data.warnings.some(w => 
            w.toLowerCase().includes('quota') || w.toLowerCase().includes('rate')
          );
          
          if (hasQuotaError) {
            toast.error('Gemini API daily quota exceeded. The free tier allows only 20 requests/day. Please upgrade your Gemini API plan or try again tomorrow.');
          } else {
            toast.warning(response.data.message || 'Optimization completed with some warnings');
          }
          console.log('Optimization warnings:', response.data.warnings);
        } else {
          toast.success(`Optimization complete! ${response.data.stats.images_generated} images generated`);
        }
        
        // Show info about what was changed
        const titleChanged = response.data.optimized.title !== response.data.original.title;
        const contentChanged = response.data.optimized.content !== response.data.original.content;
        
        if (!titleChanged && !contentChanged && response.data.stats.images_generated === 0) {
          toast.info('No changes were made. Your Gemini API daily quota (20 requests) may be exhausted. Try again tomorrow or upgrade your API plan.');
        }
      } else {
        toast.error(response.data.error || 'Optimization failed');
      }
    } catch (error) {
      console.error('Optimization error:', error);
      const errorDetail = error.response?.data?.detail || '';
      if (errorDetail.toLowerCase().includes('rate') || errorDetail.toLowerCase().includes('quota')) {
        toast.error('Gemini API quota exceeded. Free tier allows 20 requests/day. Try again tomorrow or upgrade.');
      } else {
        toast.error(errorDetail || 'AI Optimization failed. Please try again.');
      }
    } finally {
      setOptimizing(false);
      setOptimizationStep('');
    }
  };

  // Render HTML content as readable text
  const renderReadableContent = (html) => {
    return (
      <div 
        className="content-preview-area text-slate-100"
        style={{ 
          color: '#e2e8f0',
          lineHeight: '1.75'
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="loading-spinner">
        <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <p className="text-white">Post not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="post-editor-page">
      {/* Inject styles to override WordPress inline styles */}
      <style>{contentPreviewStyles}</style>
      
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur border-b border-slate-800 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/app/posts')} 
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="back-to-posts-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Posts
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-white">Edit Post</h1>
              <p className="text-xs text-slate-500">ID: {post.id}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* AI Optimize Button - The Hero */}
            {aiFeatures.ai_optimization_enabled && (
              <Button 
                onClick={runAIOptimization}
                disabled={optimizing || !hasApiKeys.gemini || !hasApiKeys.airbrush}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium px-6 shadow-lg shadow-emerald-500/25"
                data-testid="ai-optimize-btn"
              >
                {optimizing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Optimizing...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    AI Optimise
                  </>
                )}
              </Button>
            )}
            
            <Button 
              onClick={savePost} 
              disabled={saving || !hasChanges}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500"
              data-testid="save-post-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save to WordPress
            </Button>
            
            <a 
              href={post.link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-2 text-slate-400 hover:text-white transition-colors"
              data-testid="view-post-link"
            >
              <Eye className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Optimization Progress Overlay */}
      <AnimatePresence>
        {optimizing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm flex items-center justify-center"
            data-testid="optimization-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-md w-full mx-4 text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white animate-pulse" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">AI Optimization in Progress</h3>
              <p className="text-slate-400 mb-6">{optimizationStep}</p>
              <div className="flex justify-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <p className="text-xs text-slate-500 mt-6">This may take 1-2 minutes for image generation</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto p-6">
        {/* API Keys Warning */}
        {aiFeatures.ai_optimization_enabled && (!hasApiKeys.gemini || !hasApiKeys.airbrush) && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-3"
            data-testid="api-keys-warning"
          >
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-amber-200 text-sm">
                Configure your API keys to enable AI optimization.
                {!hasApiKeys.gemini && ' Missing: Gemini'}
                {!hasApiKeys.airbrush && ' Missing: Airbrush'}
              </p>
            </div>
            <Button 
              onClick={() => navigate('/app/settings')} 
              variant="outline" 
              size="sm"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
            >
              Go to Settings
            </Button>
          </motion.div>
        )}

        {/* Optimization Stats */}
        {optimizationStats && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl"
            data-testid="optimization-stats"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-emerald-200 text-sm font-medium">
                  AI Optimization Complete
                </p>
                <p className="text-emerald-400/70 text-xs mt-1">
                  {optimizationStats.images_generated} images generated • {optimizationStats.paragraphs_processed} paragraphs processed
                </p>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Editor Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title Editor */}
            <Card className="bg-slate-900 border-slate-800" data-testid="title-editor-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Title
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white text-lg font-medium placeholder:text-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20"
                  placeholder="Post title..."
                  data-testid="title-input"
                />
              </CardContent>
            </Card>

            {/* Content Display */}
            <Card className="bg-slate-900 border-slate-800" data-testid="content-editor-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    Content Preview
                  </CardTitle>
                  {hasChanges && (
                    <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded">
                      Modified
                    </span>
                  )}
                </div>
                <CardDescription className="text-slate-500 text-xs">
                  Content is displayed as it will appear on your website
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="bg-slate-800/50 rounded-xl p-6 min-h-[500px] max-h-[700px] overflow-y-auto border border-slate-700/50"
                  data-testid="content-preview"
                >
                  {editedContent ? (
                    renderReadableContent(editedContent)
                  ) : (
                    <p className="text-slate-500 italic">No content</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Post Info */}
            <Card className="bg-slate-900 border-slate-800" data-testid="post-info-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-400">Post Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Status</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    post.status === 'publish' 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {post.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Created</span>
                  <span className="text-slate-300">{new Date(post.date).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Modified</span>
                  <span className="text-slate-300">{new Date(post.modified).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Slug</span>
                  <span className="text-xs text-slate-400 truncate max-w-[150px]">{post.slug}</span>
                </div>
              </CardContent>
            </Card>

            {/* AI Optimization Info */}
            {aiFeatures.ai_optimization_enabled && (
              <Card className="bg-slate-900 border-slate-800" data-testid="ai-info-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-400" />
                    AI Optimization
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <p className="text-slate-400 text-xs leading-relaxed">
                    Click <strong className="text-emerald-400">AI Optimise</strong> to automatically:
                  </p>
                  <ul className="space-y-2 text-xs text-slate-500">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                      Generate an optimized title
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                      Improve content with headings (~600 words)
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                      Insert AI images after each paragraph
                    </li>
                  </ul>
                  <div className="pt-2 border-t border-slate-800">
                    <p className="text-xs text-slate-600">
                      Review changes before clicking "Save to WordPress"
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="bg-slate-900 border-slate-800" data-testid="quick-actions-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-400">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-slate-400 border-slate-700 hover:bg-slate-800 hover:text-white"
                  onClick={() => navigate('/app/settings')}
                  data-testid="configure-api-keys-btn"
                >
                  <Wand2 className="w-4 h-4 mr-2" /> Configure API Keys
                </Button>
                <a 
                  href={post.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 w-full px-4 py-2 border border-slate-700 rounded-md text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
                  data-testid="preview-on-site-link"
                >
                  <ExternalLink className="w-4 h-4" /> View on Website
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostEditorPage;
